import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MailWarning, X, Send, Check } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../lib/api";

/** Persistent reminder for accounts that signed in before verifying the email. */
export default function VerifyEmailBanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [state, setState] = useState("idle");
  const devUrl = (() => {
    try { return sessionStorage.getItem("mp_dev_verify_url") || ""; } catch { return ""; }
  })();

  if (!user || user.email_verified !== false || dismissed) return null;

  const resend = async () => {
    setState("sending");
    try {
      const { data } = await api.post("/auth/resend-verification", { email: user.email });
      if (data?.dev_verification_url) {
        try { sessionStorage.setItem("mp_dev_verify_url", data.dev_verification_url); } catch { /* storage off */ }
      }
      setState("sent");
    } catch {
      setState("idle");
    }
  };

  return (
    <div className="relative z-30 bg-amber-500/15 border-b border-amber-500/40" data-testid="verify-email-banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-start sm:items-center gap-2.5 text-sm text-amber-200">
          <MailWarning size={16} className="mt-0.5 sm:mt-0 shrink-0"/>
          <span>{t("verifyBanner.message", { email: user.email })}</span>
        </div>
        <div className="flex items-center gap-2">
          {devUrl && (
            <a href={devUrl} className="text-xs text-amber-300 underline font-mono-data" data-testid="verify-email-dev-link">
              {t("verifyBanner.previewLink")}
            </a>
          )}
          {state === "sent" ? (
            <span className="text-xs text-emerald-300 flex items-center gap-1" data-testid="verify-email-sent">
              <Check size={13}/>{t("verifyBanner.sent")}
            </span>
          ) : (
            <button onClick={resend} disabled={state === "sending"}
              className="text-xs px-3 py-1.5 rounded border border-amber-500/50 text-amber-200 hover:bg-amber-500/10 disabled:opacity-50 flex items-center gap-1.5"
              data-testid="verify-email-resend">
              <Send size={12}/>{state === "sending" ? t("verifyBanner.resending") : t("verifyBanner.resend")}
            </button>
          )}
          <button onClick={() => setDismissed(true)} className="text-amber-300/70 hover:text-amber-200 p-1"
            aria-label={t("verifyBanner.close")} data-testid="verify-email-close">
            <X size={15}/>
          </button>
        </div>
      </div>
    </div>
  );
}
